import { createFileRoute, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import type { LvaItemListRow } from '@dcc-erp/shared';

import { LvaFormModal } from './lva';
import { PageHead } from '../components/layout';
import { LvaDetailView } from '../components/lva/LvaDetailView';
import { useActiveCompanyId, useLvaItemByCode } from '../lib/queries';

// Scan-to-detail deep link. A printed QR label encodes
// `<origin>/lva/<code>?c=<companyId>`; scanning it lands here. The `c` search
// param pins the company the label was printed for so the lookup is correct even
// when the scanning user's *active* company differs (multi-company users). We
// fall back to the active company when `c` is absent (e.g. a hand-typed URL).
export interface LvaScanSearch {
  c?: string | undefined;
}

export const Route = createFileRoute('/lva/$code')({
  validateSearch: (search: Record<string, unknown>): LvaScanSearch => ({
    c: typeof search.c === 'string' ? search.c : undefined,
  }),
  component: LvaScanPage,
});

export function LvaScanPage() {
  const { code } = useParams({ from: '/lva/$code' });
  const { c } = Route.useSearch();
  const activeCompanyId = useActiveCompanyId();
  // The label-scoped company wins; otherwise the user's active company.
  const companyId = c ?? activeCompanyId;
  const itemQuery = useLvaItemByCode(companyId, code);
  const [editItem, setEditItem] = useState<LvaItemListRow | null>(null);

  const item = itemQuery.data;
  // Wrong-company hint: the QR pinned no company (or the active one) and the
  // active company differs from where the asset actually lives → the lookup
  // misses because the code is company-scoped server-side. Surface a switch hint
  // instead of a bare "not found" when no explicit `c` was supplied.
  const wrongCompanyHint = !item && !itemQuery.isLoading && !c && !!activeCompanyId;

  return (
    <div className="page" style={{ maxWidth: 720, margin: '0 auto' }}>
      <PageHead crumbs={['ทรัพย์สินมูลค่าต่ำ', 'สแกน QR']} title="รายละเอียดทรัพย์สิน" />

      {itemQuery.isLoading && (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <span className="muted">กำลังโหลด…</span>
        </div>
      )}

      {!itemQuery.isLoading && !item && (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>ไม่พบรหัส {code}</div>
          <div className="muted" style={{ fontSize: 14 }}>
            {wrongCompanyHint
              ? 'รหัสนี้อาจอยู่คนละบริษัท — กรุณาสลับบริษัทที่มุมขวาบนแล้วลองใหม่'
              : 'ไม่พบทรัพย์สินตามรหัสนี้ในบริษัทที่เลือก'}
          </div>
        </div>
      )}

      {item && (
        <div
          className="card"
          style={{
            background: 'white',
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          <LvaDetailView item={item} onEdit={(it) => setEditItem(it)} />
        </div>
      )}

      {editItem && <LvaFormModal item={editItem} onClose={() => setEditItem(null)} />}
    </div>
  );
}
